import { Injectable } from '@angular/core';
import { TranslationService } from '@ace/ace';

/**
 * ACE's TranslationService fires an unguarded fetch per init() and fills one shared map
 * when it resolves, so two quick language switches can finish out of order and leave the
 * map on the older language while the rest of the app is on the newer one - the mixed
 * labels seen when switching en -> ar -> de -> ar. Loads are queued here so only one runs
 * at a time and a superseded one is skipped, which makes the last write the latest language.
 */
@Injectable()
export class SequentialTranslationService extends TranslationService {
  private queue: Promise<void> = Promise.resolve();
  private latestLanguageCode = '';

  override init(languageCode: string): Promise<void> {
    const isoCode = (languageCode || 'en').toLowerCase();
    this.latestLanguageCode = isoCode;
    this.queue = this.queue
      .catch(() => undefined)
      .then(() => {
        // A newer language is already queued behind this one; loading this would only
        // overwrite the map with a language nobody asked for any more.
        if (this.latestLanguageCode !== isoCode) {
          return undefined;
        }
        return super.init(isoCode);
      });
    return this.queue;
  }
}
